import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { useDebouncedCallback } from "use-debounce";
import {
  Box,
  Modal,
  TextField,
  Button,
  IconButton,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InstagramIcon from "@mui/icons-material/Instagram";
import InfoIcon from "@mui/icons-material/Info";
import { triggerGa } from "../utils/gaUtils";
import {
  BRAND_CATEGORIES,
  emailRegex,
  USER_IN_REVIEW,
  USER_IS_WAITLISTED,
} from "../utils/constants";
import { AuthAPI } from "../api/authAPI";

const OnboardingModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setEmailUserAuthToken, user } = useAuth();
  const isInReview = user?.data?.status === USER_IN_REVIEW;
  const isWaitlisted = user?.data?.status === USER_IS_WAITLISTED;
  const brandName = user?.data?.parent_brand?.name;
  const signedUpEmail = user?.data?.email;
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Account Creation
    yourName: "",
    designation: "",
    designationOther: "",
    workEmail: "",
    countryCode: "+91",
    contactNumber: "",
    hearAboutUs: "",
    hearAboutUsOther: "",

    // Step 2: Brand Information
    brandName: "",
    brandWebsite: "",
    brandInstagram: "",
    brandCategory: "",

    // Step 3: Business Details
    gmvRange: "",
    aov: "",
    influencerCount: "",
    interest: [],
  });

  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationStatus, setValidationStatus] = useState({});
  const [isValidating, setIsValidating] = useState({});

  const gmvOptions = [
    "< ₹10 Lakhs",
    "₹10 Lakhs - ₹50 Lakhs",
    "₹50 Lakhs - ₹1 Cr",
    "₹1 Cr - ₹10 Cr",
    "₹10 Cr - ₹100 Cr",
    "₹100 Cr+",
  ];

  const aovOptions = ["0-500", "500-1000", "1000-2000", "2000+"];

  const influencerOptions = [
    "Less than 10 influencers",
    "10 - 50 influencers",
    "50 - 100 influencers",
    "100 - 500 influencers",
    "500+ influencers",
  ];

  const interestOptions = ["Affiliate Sales", "Influencer Collabs"];

  const hearAboutUsOptions = [
    "Another brand",
    "Content Creator",
    "Social Media Post",
    "Ad",
    "Internal Team member",
    "Other (please specify)",
  ];

  const designationOptions = [
    "Founder / Owner",
    "C-suite (CEO / CMO / COO)",
    "VP / Director — Marketing & Growth",
    "Head / Manager - Influencer & Creator Marketing",
    "Head / Manager - Performance Marketing",
    "Brand Marketing Manager",
    "Social Media / Community Manager",
    "E-commerce / D2C Manager (Shopify/Marketplace)",
    "Partnerships / Business Development",
    "Product / Category Manager",
    "Agency / Partner (on behalf of brand)",
    "Other (please specify)",
  ];

  // Fire GA event when popup is in review or waitlisted state
  useEffect(() => {
    if (isOpen && (isInReview || isWaitlisted)) {
      const label = isInReview ? "review" : "waitlisted";
      triggerGa({
        category: "Signup",
        action: "popup_view",
        label: label,
        page: "signup_form",
        screenName: "signup_form",
      });
    }
  }, [isOpen, isInReview, isWaitlisted]);

  const handleLogout = () => {
    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("user_type");
    setEmailUserAuthToken(null);
    queryClient.clear();
    navigate("/login", { replace: true });
  };

  const handleClose = () => {
    triggerGa({
      category: "Signup",
      action: "button_click",
      label: "close",
      label2: "",
      page: "Signup",
      screenName:
        currentStep === 1
          ? "userinfo"
          : currentStep === 2
            ? "brandinfo"
            : "branddetails",
    });
    handleLogout();
    onClose();
  };
  // Debounced server validation
  const validateFieldOnServer = useDebouncedCallback(
    async (fieldType, value) => {
      if (!value || value.trim() === "") {
        return;
      }

      console.log(`Validating ${fieldType} with value: ${value}`);
      setIsValidating((prev) => ({ ...prev, [fieldType]: true }));

      try {
        const response = await AuthAPI.validateOnboardingField(
          fieldType,
          value,
        );
        console.log(`Validation response for ${fieldType}:`, response);

        if (response.success) {
          console.log(`Validation SUCCESS for ${fieldType}`);
          setValidationStatus((prev) => {
            const newStatus = { ...prev, [fieldType]: "valid" };
            console.log("Setting validation status:", newStatus);
            return newStatus;
          });

          // Clear the error using the correct field name mapping
          const errorFieldMap = {
            email: "workEmail",
            brand_name: "brandName",
            website: "brandWebsite",
          };
          const errorField = errorFieldMap[fieldType] || fieldType;
          console.log(
            `Clearing error for ${errorField} after successful validation`,
          );
          setErrors((prev) => ({ ...prev, [errorField]: "" }));
        } else {
          console.log(`Validation FAILED for ${fieldType}:`, response.message);
          setValidationStatus((prev) => {
            const newStatus = { ...prev, [fieldType]: "invalid" };
            console.log("Setting validation status:", newStatus);
            return newStatus;
          });
          // Map server field types to form field names
          const errorFieldMap = {
            email: "workEmail",
            brand_name: "brandName",
            website: "brandWebsite",
          };
          const errorField = errorFieldMap[fieldType] || fieldType;
          const errorMessage = response.message || "This field is invalid";
          console.log(`Setting error for ${errorField}:`, errorMessage);
          setErrors((prev) => {
            const newErrors = { ...prev, [errorField]: errorMessage };
            console.log("Setting errors:", newErrors);
            return newErrors;
          });
        }
      } catch (error) {
        console.error(`Error validating ${fieldType}:`, error);

        // Only handle validation errors (400 status codes with error message)
        if (
          error.response &&
          error.response.status === 400 &&
          error.response.data
        ) {
          const errorFieldMap = {
            email: "workEmail",
            brand_name: "brandName",
            website: "brandWebsite",
          };
          const errorField = errorFieldMap[fieldType] || fieldType;
          const errorMessage =
            error.response.data.message || "This field is invalid";
          console.log(`Setting error for ${errorField}:`, errorMessage);

          setValidationStatus((prev) => ({ ...prev, [fieldType]: "invalid" }));
          setErrors((prev) => {
            const newErrors = { ...prev, [errorField]: errorMessage };
            console.log("Setting errors:", newErrors);
            return newErrors;
          });
        } else {
          // For other errors (network issues, 500 errors, etc), don't set validation state
          console.error("Non-validation error occurred:", error);
          setValidationStatus((prev) => ({ ...prev, [fieldType]: undefined }));
        }
      } finally {
        setIsValidating((prev) => ({ ...prev, [fieldType]: false }));
      }
    },
    500,
  );

  const handleValidation = (name, value) => {
    let errorMessage = "";
    console.log(`Client validation for ${name}:`, value);

    switch (name) {
      case "brandWebsite":
        const urlRegex =
          /^(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&/=]*)$/;
        errorMessage =
          value.trim() === ""
            ? "Brand Website is required"
            : !urlRegex.test(value)
              ? "Enter a valid domain (e.g., example.com)"
              : "";

        // Server validation for website
        if (!errorMessage && value.trim()) {
          // Clear any previous client-side errors before server validation
          setErrors((prev) => ({ ...prev, [name]: "" }));
          // Add https:// prefix for server validation
          validateFieldOnServer("website", `https://${value}`);
          return; // Don't set errors here, let server validation handle it
        }
        break;

      case "brandName":
        errorMessage = value.trim() === "" ? "Brand Name is required" : "";

        // Server validation for brand name
        if (!errorMessage && value.trim()) {
          // Clear any previous client-side errors before server validation
          setErrors((prev) => ({ ...prev, [name]: "" }));
          validateFieldOnServer("brand_name", value);
          return; // Don't set errors here, let server validation handle it
        }
        break;

      case "yourName":
        const nameRegex = /^[A-Za-z\s]+$/;
        errorMessage =
          value.trim() === ""
            ? "Name is required"
            : !nameRegex.test(value)
              ? "Only letters and spaces allowed"
              : "";
        break;

      case "contactNumber": {
        const digits = value.replace(/\s+/g, "");
        const phoneRegex =
          formData.countryCode === "+91" ? /^[1-9]\d{9}$/ : /^[0-9]{7,15}$/;
        errorMessage =
          value.trim() === ""
            ? "Contact number is required"
            : !phoneRegex.test(digits)
              ? formData.countryCode === "+91"
                ? "Enter a valid 10-digit mobile number"
                : "Enter a valid contact number"
              : "";
        break;
      }

      case "workEmail":
        errorMessage =
          value.trim() === ""
            ? "Email is required"
            : !emailRegex.test(value)
              ? "Enter a valid email address"
              : "";

        // Server validation for email
        if (!errorMessage && value.trim()) {
          // Clear any previous client-side errors before server validation
          setErrors((prev) => ({ ...prev, [name]: "" }));
          validateFieldOnServer("email", value);
          return; // Don't set errors here, let server validation handle it
        }
        break;

      default:
        break;
    }

    // Only set errors if we didn't do server validation (server validation returns early)
    setErrors((prevErrors) => ({ ...prevErrors, [name]: errorMessage }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log(`handleChange for ${name}:`, value);

    // For brandWebsite, strip https:// or http:// if user types it
    let processedValue = value;
    if (name === "brandWebsite") {
      processedValue = value.replace(/^https?:\/\//i, "");
    }

    // Clear designationOther if designation changes from "Other" to something else
    if (name === "designation" && value !== "Other") {
      setFormData((prev) => ({
        ...prev,
        [name]: processedValue,
        designationOther: "",
      }));
    }
    // Clear hearAboutUsOther if hearAboutUs changes from "Other (please specify)" to something else
    else if (name === "hearAboutUs" && value !== "Other (please specify)") {
      setFormData((prev) => ({
        ...prev,
        [name]: processedValue,
        hearAboutUsOther: "",
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: processedValue }));
    }

    if (typeof processedValue === "string") {
      // Clear server validation errors if field is now empty
      if (!processedValue.trim()) {
        if (name === "workEmail") {
          setValidationStatus((prev) => ({ ...prev, email: undefined }));
        } else if (name === "brandName") {
          setValidationStatus((prev) => ({ ...prev, brand_name: undefined }));
        } else if (name === "brandWebsite") {
          setValidationStatus((prev) => ({ ...prev, website: undefined }));
        }
      }
      // Only clear validation status if the value actually changed
      // Don't clear 'invalid' status - let it stay until new validation completes
      else if (name === "workEmail" && validationStatus.email === "valid") {
        console.log("Clearing email validation status (was valid)");
        setValidationStatus((prev) => ({ ...prev, email: undefined }));
      } else if (
        name === "brandName" &&
        validationStatus.brand_name === "valid"
      ) {
        console.log("Clearing brand_name validation status (was valid)");
        setValidationStatus((prev) => ({ ...prev, brand_name: undefined }));
      } else if (
        name === "brandWebsite" &&
        validationStatus.website === "valid"
      ) {
        console.log("Clearing website validation status (was valid)");
        setValidationStatus((prev) => ({ ...prev, website: undefined }));
      }
    }
    handleValidation(name, processedValue);
  };

  const isStepValid = (step) => {
    switch (step) {
      case 1:
        // Check if designation "Other" is selected and designationOther is filled
        const isDesignationValid =
          formData.designation &&
          (formData.designation !== "Other" ||
            (formData.designation === "Other" &&
              formData.designationOther.trim()));

        // Check if hearAboutUs "Other (please specify)" is selected and hearAboutUsOther is filled
        const isHearAboutUsValid =
          formData.hearAboutUs &&
          (formData.hearAboutUs !== "Other (please specify)" ||
            (formData.hearAboutUs === "Other (please specify)" &&
              formData.hearAboutUsOther.trim()));

        return (
          formData.yourName &&
          isDesignationValid &&
          formData.workEmail &&
          formData.contactNumber &&
          isHearAboutUsValid &&
          !errors.yourName &&
          !errors.workEmail &&
          !errors.contactNumber &&
          validationStatus.email !== "invalid"
        );
      case 2:
        return (
          formData.brandName &&
          formData.brandWebsite &&
          formData.brandCategory &&
          !errors.brandName &&
          !errors.brandWebsite &&
          validationStatus.brand_name !== "invalid" &&
          validationStatus.website !== "invalid"
        );
      case 3:
        return (
          formData.gmvRange &&
          formData.aov &&
          formData.influencerCount &&
          formData.interest &&
          formData.interest.length > 0
        );
      default:
        return false;
    }
  };

  const handleNext = () => {
    triggerGa({
      category: "Signup",
      action: "button_click",
      label: "next",
      label2: "",
      page: "Signup",
      screenName:
        currentStep === 1
          ? "userinfo"
          : currentStep === 2
            ? "brandinfo"
            : "branddetails",
    });
    if (currentStep < 3 && isStepValid(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    triggerGa({
      category: "Signup",
      action: "button_click",
      label: "back",
      label2: "",
      page: "Signup",
      screenName:
        currentStep === 1
          ? "userinfo"
          : currentStep === 2
            ? "brandinfo"
            : "branddetails",
    });
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isStepValid(3)) {
      return;
    }

    setIsSubmitting(true);

    triggerGa({
      category: "Signup",
      action: "button_click",
      label: "create_account",
      label2: "",
      page: "Signup",
      screenName: "branddetails",
    });

    try {
      // Add https:// prefix to brandWebsite before sending to backend
      const submitData = {
        ...formData,
        brandWebsite: formData.brandWebsite
          ? `https://${formData.brandWebsite}`
          : "",
      };

      const response = await AuthAPI.createOnboardingUser(submitData);

      // Store the token in sessionStorage (similar to login flow)
      if (response.data?.token) {
        sessionStorage.setItem("authToken", response.data.token);
        sessionStorage.setItem("user_type", response.data.user_type);

        // Update the auth context state
        setEmailUserAuthToken(response.data.token);

        // Invalidate and refetch user data
        queryClient.removeQueries({ queryKey: ["currentUser"] });
        await queryClient.prefetchQuery({
          queryKey: ["currentUser"],
          queryFn: () => AuthAPI.getCurrentUser(),
        });
      }

      triggerGa({
        category: "Onboarding",
        action: "account_created",
        label: "multi_step_form",
        label2: formData.brandCategory,
        page: "login",
        screenName: "onboarding_complete",
      });

      setIsSubmitting(false);
      setSnackbarOpen(true);
      setFormData({
        yourName: "",
        designation: "",
        designationOther: "",
        workEmail: "",
        countryCode: "+91",
        contactNumber: "",
        hearAboutUs: "",
        hearAboutUsOther: "",
        brandName: "",
        brandWebsite: "",
        brandInstagram: "",
        brandCategory: "",
        gmvRange: "",
        aov: "",
        influencerCount: "",
        interest: [],
      });
      setCurrentStep(1);
      onClose();

      // Clear integration data from localStorage for fresh start
      localStorage.removeItem("integration_data");
      localStorage.removeItem("integration_setup");
    } catch (error) {
      console.error("Error:", error);
      const errorMessage =
        error.response?.data?.message || error.message || "An error occurred";
      alert("An error occurred: " + errorMessage);
      setIsSubmitting(false);
    }
  };

  const renderProgressBar = () => (
    <Box sx={styles.progressContainer}>
      {[1, 2, 3].map((step) => (
        <Box
          key={step}
          sx={{
            ...styles.progressBar,
            backgroundColor: step <= currentStep ? "#EE4D37" : "transparent",
            border: step <= currentStep ? "none" : "1px solid #EE4D37",
          }}
        />
      ))}
      <Typography sx={styles.progressText}>{currentStep}/3</Typography>
    </Box>
  );

  const renderStep3 = () => (
    <Box>
      <Typography variant="h5" sx={styles.stepTitle}>
        Business Details
      </Typography>
      <Typography variant="body2" sx={styles.stepSubtitle}>
        Help us understand your business better
      </Typography>

      <Box sx={styles.businessField}>
        <Typography variant="body2" sx={styles.fieldLabel}>
          Gross Monthly Sales
        </Typography>
        <FormControl fullWidth sx={{ mt: 0.5 }}>
          <Select
            name="gmvRange"
            value={formData.gmvRange}
            onChange={handleChange}
            displayEmpty
            sx={styles.hearAboutUsSelect}
          >
            <MenuItem value="" disabled>
              Select an option
            </MenuItem>
            {gmvOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={styles.businessField}>
        <Typography variant="body2" sx={styles.fieldLabel}>
          AOV
        </Typography>
        <FormControl fullWidth sx={{ mt: 0.5 }}>
          <Select
            name="aov"
            value={formData.aov}
            onChange={handleChange}
            displayEmpty
            sx={styles.hearAboutUsSelect}
          >
            <MenuItem value="" disabled>
              Select an option
            </MenuItem>
            {aovOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={styles.businessField}>
        <Typography variant="body2" sx={styles.fieldLabel}>
          Number of Influencers you work with
        </Typography>
        <FormControl fullWidth sx={{ mt: 0.5 }}>
          <Select
            name="influencerCount"
            value={formData.influencerCount}
            onChange={handleChange}
            displayEmpty
            sx={styles.hearAboutUsSelect}
          >
            <MenuItem value="" disabled>
              Select an option
            </MenuItem>
            {influencerOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" sx={{ color: "grey", mt: 1 }}>
          Include both direct relationships and agency managed influencers
        </Typography>
      </Box>

      <Box sx={styles.businessField}>
        <Typography variant="body2" sx={styles.fieldLabel}>
          What are you most interested in?
        </Typography>
        <FormControl fullWidth sx={{ mt: 0.5 }}>
          <Select
            name="interest"
            value={formData.interest.length > 0 ? formData.interest[0] : ""}
            onChange={(e) => {
              // Store as array with single item
              handleChange({
                target: {
                  name: "interest",
                  value: [e.target.value],
                },
              });
            }}
            displayEmpty
            sx={styles.hearAboutUsSelect}
          >
            <MenuItem value="" disabled>
              Select an option
            </MenuItem>
            {interestOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" sx={{ color: "grey", mt: 1 }}>
          This helps us tailor your onboarding experience
        </Typography>
      </Box>
    </Box>
  );

  const renderStep2 = () => (
    <Box>
      <Typography variant="h5" sx={styles.stepTitle}>
        Brand Information
      </Typography>
      <Typography variant="body2" sx={styles.stepSubtitle}>
        Tell us about your brand
      </Typography>

      <Box sx={styles.brandNameField}>
        <Typography variant="body2" sx={styles.fieldLabel}>
          Brand Name
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          name="brandName"
          value={formData.brandName}
          onChange={handleChange}
          error={!!errors.brandName}
          placeholder="Enter brand name here"
          sx={{ ...styles.input, mt: 0.5 }}
          InputProps={{
            endAdornment: formData.brandName ? (
              <InputAdornment position="end">
                {isValidating.brand_name && <CircularProgress size={20} />}
                {!isValidating.brand_name &&
                  validationStatus.brand_name === "valid" &&
                  !errors.brandName && (
                    <CheckCircleIcon color="success" sx={{ fontSize: 20 }} />
                  )}
                {!isValidating.brand_name &&
                  (validationStatus.brand_name === "invalid" ||
                    errors.brandName) && (
                    <ErrorIcon color="error" sx={{ fontSize: 20 }} />
                  )}
              </InputAdornment>
            ) : null,
            sx: { pr: 1 },
          }}
        />
        {errors.brandName && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
            {errors.brandName}
          </Typography>
        )}
      </Box>

      <Box sx={styles.brandWebsiteField}>
        <Typography variant="body2" sx={styles.fieldLabel}>
          Brand website
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          name="brandWebsite"
          value={formData.brandWebsite}
          onChange={handleChange}
          error={!!errors.brandWebsite}
          placeholder="Enter brand website here"
          sx={{ ...styles.input, mt: 0.5 }}
          InputProps={{
            endAdornment: formData.brandWebsite ? (
              <InputAdornment position="end">
                {isValidating.website && <CircularProgress size={20} />}
                {!isValidating.website &&
                  validationStatus.website === "valid" &&
                  !errors.brandWebsite && (
                    <CheckCircleIcon color="success" sx={{ fontSize: 20 }} />
                  )}
                {!isValidating.website &&
                  (validationStatus.website === "invalid" ||
                    errors.brandWebsite) && (
                    <ErrorIcon color="error" sx={{ fontSize: 20 }} />
                  )}
              </InputAdornment>
            ) : null,
            sx: { pr: 1 },
          }}
        />
        {errors.brandWebsite && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
            {errors.brandWebsite}
          </Typography>
        )}
      </Box>

      <Box sx={styles.instagramField}>
        <Typography variant="body2" sx={styles.fieldLabel}>
          Brand Instagram
        </Typography>
        <Box sx={{ ...styles.instagramInput, mt: 0.5 }}>
          <Box sx={styles.instagramPrefix}>
            <Typography
              variant="body2"
              sx={{ color: "grey", fontSize: "14px" }}
            >
              instagram.com/
            </Typography>
          </Box>
          <TextField
            fullWidth
            variant="outlined"
            name="brandInstagram"
            value={formData.brandInstagram}
            onChange={handleChange}
            placeholder="yourbrand"
            sx={styles.phoneNumberField}
          />
        </Box>
      </Box>

      <Box sx={styles.brandCategoryField}>
        <Typography variant="body2" sx={styles.fieldLabel}>
          Brand category
        </Typography>
        <FormControl fullWidth sx={{ mt: 0.5 }}>
          <Select
            name="brandCategory"
            value={formData.brandCategory}
            onChange={handleChange}
            displayEmpty
            sx={styles.hearAboutUsSelect}
          >
            <MenuItem value="" disabled>
              Select a category
            </MenuItem>
            {BRAND_CATEGORIES.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );

  const renderReviewScreen = () => (
    <Box
      sx={{ display: "flex", flexDirection: "column", height: "100%", mt: 3 }}
    >
      <Typography variant="h5" sx={styles.stepTitle}>
        {brandName || "Your brand"} is in review
      </Typography>
      <Typography variant="body2" sx={styles.stepSubtitle}>
        Thanks for showing interest in Wishlink. Our team is reviewing your
        application to ensure the best experience for you.
      </Typography>

      <Box sx={styles.reviewInfoBox}>
        <Box sx={styles.reviewInfoHeader}>
          <InfoIcon
            sx={{
              fontSize: "20px",
              color: "#144cc7",
            }}
          />
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#144cc7",
              lineHeight: "1.4",
            }}
          >
            You will hear from us in 4 to 5 business days
          </Typography>
        </Box>

        <Box sx={styles.reviewInfoContent}>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              lineHeight: "1.6",
              color: "#000",
              mb: 1.5,
            }}
          >
            Once approved, you'll receive an email invitation to complete your
            onboarding and start collaborating with creators.
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              lineHeight: "1.6",
              color: "#000",
              mb: 1.5,
            }}
          >
            Log back in anytime to check your status.
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              lineHeight: "1.6",
              color: "#000",
            }}
          >
            Kindly check your email ({signedUpEmail}) for login credentials and
            further updates
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const renderWaitlistScreen = () => (
    <Box
      sx={{ display: "flex", flexDirection: "column", height: "100%", mt: 3 }}
    >
      <Typography variant="h5" sx={styles.stepTitle}>
        {brandName || "Your brand"} is on the Waitlist
      </Typography>
      <Typography variant="body2" sx={styles.stepSubtitle}>
        Oops! After careful consideration, we have decided to add you to our
        onboarding waitlist for now.
      </Typography>

      <Box sx={styles.reviewInfoBox}>
        <Box sx={styles.reviewInfoHeader}>
          <InfoIcon
            sx={{
              fontSize: "20px",
              color: "#144cc7",
            }}
          />
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#144cc7",
              lineHeight: "1.4",
            }}
          >
            We'll review again soon
          </Typography>
        </Box>

        <Box sx={styles.reviewInfoContent}>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              lineHeight: "1.6",
              color: "#000",
              mb: 1.5,
            }}
          >
            We regularly revisit waitlisted applications and will reach out once
            your brand becomes eligible to join.
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              lineHeight: "1.6",
              color: "#000",
            }}
          >
            Log back in anytime to check your status.
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const renderStep1 = () => (
    <Box>
      <Typography variant="h5" sx={styles.stepTitle}>
        Create your account
      </Typography>
      <Typography variant="body2" sx={styles.stepSubtitle}>
        Tell us about yourself
      </Typography>

      <Box sx={styles.nameField}>
        <Typography variant="body2" sx={styles.fieldLabel}>
          Your Name
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          name="yourName"
          value={formData.yourName}
          onChange={handleChange}
          error={!!errors.yourName}
          placeholder="Enter your name here"
          sx={{ ...styles.input, mt: 0.5 }}
        />
        {errors.yourName && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
            {errors.yourName}
          </Typography>
        )}
      </Box>

      <Box sx={styles.emailField}>
        <Typography variant="body2" sx={styles.fieldLabel}>
          Work email
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          name="workEmail"
          value={formData.workEmail}
          onChange={handleChange}
          error={!!errors.workEmail}
          placeholder="Enter work email here"
          sx={{ ...styles.input, mt: 0.5 }}
          InputProps={{
            endAdornment: formData.workEmail ? (
              <InputAdornment position="end">
                {isValidating.email && <CircularProgress size={20} />}
                {!isValidating.email &&
                  validationStatus.email === "valid" &&
                  !errors.workEmail && (
                    <CheckCircleIcon color="success" sx={{ fontSize: 20 }} />
                  )}
                {!isValidating.email &&
                  (validationStatus.email === "invalid" ||
                    errors.workEmail) && (
                    <ErrorIcon color="error" sx={{ fontSize: 20 }} />
                  )}
              </InputAdornment>
            ) : null,
            sx: { pr: 1 },
          }}
        />
        {errors.workEmail && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
            {errors.workEmail}
          </Typography>
        )}
      </Box>

      <Box sx={styles.phoneField}>
        <Typography variant="body2" sx={styles.fieldLabel}>
          Contact number
        </Typography>
        <Box sx={{ ...styles.phoneInput, mt: 0.5 }}>
          <Box sx={styles.countryCodeBox}>
            <Box
              sx={{
                width: 24,
                height: 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "18px",
              }}
            >
              🇮🇳
            </Box>
            <Select
              name="countryCode"
              value={formData.countryCode}
              onChange={handleChange}
              sx={styles.countryCodeSelect}
            >
              <MenuItem value="+91">+91</MenuItem>
            </Select>
          </Box>
          <TextField
            fullWidth
            variant="outlined"
            name="contactNumber"
            value={formData.contactNumber}
            onChange={handleChange}
            error={!!errors.contactNumber}
            placeholder="Enter contact here"
            sx={styles.phoneNumberField}
          />
        </Box>
        {errors.contactNumber && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
            {errors.contactNumber}
          </Typography>
        )}
      </Box>

      <Box sx={styles.emailField}>
        <Typography variant="body2" sx={styles.fieldLabel}>
          Designation
        </Typography>
        <FormControl fullWidth sx={{ mt: 0.5 }}>
          <Select
            name="designation"
            value={formData.designation}
            onChange={handleChange}
            displayEmpty
            sx={styles.hearAboutUsSelect}
          >
            <MenuItem value="" disabled>
              Select your designation
            </MenuItem>
            {designationOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Show free-text field when "Other" is selected */}
        {formData.designation === "Other" && (
          <TextField
            fullWidth
            variant="outlined"
            name="designationOther"
            value={formData.designationOther}
            onChange={handleChange}
            placeholder="Please specify your designation"
            sx={{ ...styles.input, mt: 1 }}
          />
        )}
      </Box>

      <Box sx={styles.hearAboutUsField}>
        <Typography variant="body2" sx={styles.fieldLabel}>
          Where did you hear about us?
        </Typography>
        <FormControl fullWidth sx={{ mt: 0.5 }}>
          <Select
            name="hearAboutUs"
            value={formData.hearAboutUs}
            onChange={handleChange}
            displayEmpty
            sx={styles.hearAboutUsSelect}
          >
            <MenuItem value="" disabled>
              Select an option
            </MenuItem>
            {hearAboutUsOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Show free-text field when "Other (please specify)" is selected */}
        {formData.hearAboutUs === "Other (please specify)" && (
          <TextField
            fullWidth
            variant="outlined"
            name="hearAboutUsOther"
            value={formData.hearAboutUsOther}
            onChange={handleChange}
            placeholder="Please specify (max 120 characters)"
            inputProps={{ maxLength: 120 }}
            sx={{ ...styles.input, mt: 1 }}
            helperText={`${formData.hearAboutUsOther.length}/120 characters`}
          />
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <Modal open={isOpen} onClose={onClose}>
        <Box sx={styles.modal}>
          <IconButton onClick={handleClose} sx={styles.closeButton}>
            <CloseIcon />
          </IconButton>

          {!isInReview && !isWaitlisted && renderProgressBar()}

          <Box sx={styles.content}>
            {isInReview ? (
              renderReviewScreen()
            ) : isWaitlisted ? (
              renderWaitlistScreen()
            ) : (
              <>
                {currentStep === 1 && renderStep1()}
                {currentStep === 2 && renderStep2()}
                {currentStep === 3 && renderStep3()}
              </>
            )}
          </Box>

          <Box sx={styles.buttonContainer}>
            {isInReview || isWaitlisted ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  width: "100%",
                }}
              >
                <Button
                  variant="contained"
                  sx={styles.nextButton}
                  onClick={handleClose}
                >
                  Got It
                </Button>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "grey",
                      lineHeight: "1.5",
                    }}
                  >
                    You are signed in as{" "}
                    <Box component="span" sx={{ color: "#000" }}>
                      {signedUpEmail || "user@example.com"}
                    </Box>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#d1d1d1",
                      lineHeight: "1.5",
                    }}
                  >
                    |
                  </Typography>
                  <Typography
                    onClick={handleLogout}
                    sx={{
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "#EE4D37",
                      lineHeight: "1.5",
                      cursor: "pointer",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Log Out
                  </Typography>
                </Box>
              </Box>
            ) : (
              <>
                {currentStep > 1 && (
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    startIcon={<ArrowBackIcon />}
                    sx={{
                      ...styles.backButton,
                      flex: 1,
                      height: "48px",
                      fontSize: "16px",
                      fontWeight: 500,
                      textTransform: "none",
                    }}
                  >
                    Back
                  </Button>
                )}

                {currentStep === 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={!isStepValid(currentStep)}
                    endIcon={<ArrowForwardIcon />}
                    sx={styles.nextButton}
                  >
                    Next
                  </Button>
                ) : currentStep < 3 ? (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={!isStepValid(currentStep)}
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      ...styles.nextButton,
                      flex: 1,
                      ml: 3,
                    }}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!isStepValid(3) || isSubmitting}
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      ...styles.createButton,
                      flex: 1,
                      ml: 3,
                    }}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Join Waitlist"
                    )}
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Account created successfully!
        </Alert>
      </Snackbar>
    </>
  );
};

const styles = {
  modal: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 500,
    maxHeight: "90vh",
    bgcolor: "background.paper",
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
    borderRadius: "8px",
    p: 4,
    outline: "none",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  closeButton: {
    position: "absolute",
    left: 20,
    top: 8,
    color: "#666",
    zIndex: 1,
  },
  scrollableContent: {
    overflowY: "auto",
    flex: 1,
    pr: 1,
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "4px",
      "&:hover": {
        background: "#555",
      },
    },
  },
  progressContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    mb: 3,
    mt: 3,
    flexShrink: 0,
  },
  progressBar: {
    flex: 1,
    height: "5px",
    borderRadius: "9px",
    transition: "all 0.3s ease",
  },
  progressText: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#0D0D0D",
    letterSpacing: "0.2px",
    textTransform: "uppercase",
    width: "32px",
    textAlign: "center",
  },
  content: {
    minHeight: 400,
    overflowY: "auto",
    flex: 1,
    mb: 2,
    pr: 1,
  },
  stepTitle: {
    textAlign: "left",
    fontWeight: 600,
    fontSize: "24px",
    lineHeight: "28px",
    mb: 1.5,
  },
  stepSubtitle: {
    textAlign: "left",
    color: "grey",
    fontSize: "14px",
    lineHeight: "20px",
    mb: 3,
  },
  fieldLabel: {
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: "18px",
    letterSpacing: "0.2px",
    color: "#000",
    mb: 1,
  },
  nameField: {
    mb: 2,
  },
  nameInput: {
    display: "flex",
    gap: "8px",
  },
  titleDropdown: {
    width: "103px",
    height: "40px",
    "& .MuiSelect-select": {
      color: "grey",
      fontSize: "14px",
      lineHeight: "20px",
      padding: "8px",
    },
  },
  nameTextField: {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      height: "40px",
    },
    "& .MuiInputBase-input": {
      fontSize: "14px",
      color: "grey",
      padding: "8px 12px",
      "&::placeholder": {
        color: "grey",
        opacity: 1,
      },
    },
  },
  emailField: {
    mb: 2,
  },
  input: {
    "& .MuiOutlinedInput-root": {
      height: "40px",
    },
    "& .MuiInputBase-input": {
      fontSize: "14px",
      color: "grey",
      padding: "8px 12px",
      "&::placeholder": {
        color: "grey",
        opacity: 1,
      },
    },
  },
  phoneField: {
    mb: 2,
  },
  phoneInput: {
    display: "flex",
    gap: "8px",
  },
  countryCodeBox: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    border: "1px solid #D4D4D4",
    borderRadius: "4px",
    padding: "8px",
    height: "40px",
  },
  countryCodeSelect: {
    "& .MuiSelect-select": {
      color: "grey",
      fontSize: "14px",
      lineHeight: "20px",
      padding: 0,
      minWidth: "40px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  countryCode: {
    width: 100,
    mr: 1,
  },
  phoneNumberField: {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      height: "40px",
    },
    "& .MuiInputBase-input": {
      fontSize: "14px",
      color: "grey",
      padding: "8px 12px",
      "&::placeholder": {
        color: "grey",
        opacity: 1,
      },
    },
  },
  hearAboutUsField: {
    mb: 2,
  },
  hearAboutUsSelect: {
    height: "40px",
    "& .MuiSelect-select": {
      color: "grey",
      fontSize: "14px",
      lineHeight: "20px",
      padding: "8px",
    },
  },
  brandNameField: {
    mb: 2,
  },
  brandWebsiteField: {
    mb: 2,
  },
  instagramField: {
    mb: 2,
  },
  instagramInput: {
    display: "flex",
    gap: "8px",
  },
  instagramPrefix: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#E5E5E5",
    border: "1px solid #D4D4D4",
    borderRadius: "4px",
    padding: "8px",
    height: "40px",
  },
  instagramTextfield: {
    ml: 1,
    flex: 1,
  },
  brandCategoryField: {
    mb: 2,
  },
  businessField: {
    mb: 2,
  },
  reviewInfoBox: {
    background:
      "linear-gradient(90deg, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.5)), linear-gradient(90deg, rgba(194, 208, 242, 1), rgba(194, 208, 242, 1))",
    borderRadius: "8px",
    padding: "20px",
    mt: 3,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  reviewInfoHeader: {
    display: "flex",
    alignItems: "center",
    gap: 1,
  },
  reviewInfoContent: {
    display: "flex",
    flexDirection: "column",
  },
  successMessage: {
    backgroundColor: "#f0f9ff",
    border: "1px solid #0ea5e9",
    borderRadius: "8px",
    p: 2,
    mt: 2,
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "space-between",
    pt: 2,
    borderTop: "1px solid #E5E5E5",
    flexShrink: 0,
  },
  backButton: {
    color: "#666",
    borderColor: "#ddd",
  },
  nextButton: {
    backgroundColor: "#EE4D37",
    color: "white",
    width: "100%",
    height: "48px",
    fontSize: "16px",
    fontWeight: 500,
    textTransform: "none",
    borderRadius: "4px",
    "&:hover": {
      backgroundColor: "#d63f2a",
    },
    "&:disabled": {
      backgroundColor: "#E5E5E5",
      color: "#999",
    },
  },
  createButton: {
    backgroundColor: "#EE4D37",
    "&:hover": {
      backgroundColor: "#d63f2a",
    },
  },
};

export default OnboardingModal;
